/** Detail summary and popup edits for an overlay timeline clip (artifacts, B-ROLL panes, titles). */

import { brollTypeMeta } from '@/lib/reelsShortsBroll';
import { isCardOverlayClip } from '@/lib/cardCarousel';
import { sanitizeArtifactMotionStyle } from '@/lib/artifactStickerMotion';
import { sanitizeBrollVisualStyle } from '@/lib/reelsShortsBroll';
import { exportTargetSizeForAspect } from '@/lib/videoExportFraming';
import {
  hostOverlayClipEndTime,
  hostOverlayPlaybackUrl,
  isArtifactStickerClip,
  isBackgroundOverlayClip,
  isLayoutBrollClip,
  isTitleOverlayClip,
} from '@/lib/videoEditorHostOverlays';

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB'];

/** `1:04.35` — seconds keep 2 decimals so tight artifact triggers stay readable. */
export function formatOverlayTimecode(seconds) {
  const total = Math.max(0, Number(seconds) || 0);
  const mins = Math.floor(total / 60);
  const secs = total - mins * 60;
  return `${mins}:${secs.toFixed(2).padStart(5, '0')}`;
}

export function formatOverlayDuration(seconds) {
  const total = Math.max(0, Number(seconds) || 0);
  return `${total.toFixed(2)}s`;
}

export function formatPercent(fraction, digits = 1) {
  const value = (Number(fraction) || 0) * 100;
  return `${value.toFixed(digits)}%`;
}

export function formatByteSize(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '';
  let scaled = value;
  let unit = 0;
  while (scaled >= 1024 && unit < BYTE_UNITS.length - 1) {
    scaled /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? Math.round(scaled) : scaled.toFixed(1)} ${BYTE_UNITS[unit]}`;
}

export function overlayClipLaneLabel(clip) {
  if (isTitleOverlayClip(clip)) return 'Title';
  if (isLayoutBrollClip(clip)) return 'B-ROLL';
  if (isBackgroundOverlayClip(clip)) return 'Background';
  if (clip?.carouselGroupId) return 'Cards';
  if (isArtifactStickerClip(clip)) return 'Artifacts';
  return 'Overlay';
}

export function overlayClipKindLabel(clip) {
  const type = clip?.brollType || clip?.broll_type || '';
  return brollTypeMeta(type)?.label || (type ? String(type).replace(/_/g, ' ') : overlayClipLaneLabel(clip));
}

/** Strips query strings so presigned URLs still yield a readable file name. */
export function outputFileNameFromUrl(url) {
  const raw = String(url || '').split(/[?#]/)[0];
  const tail = raw.split('/').filter(Boolean).pop() || '';
  try {
    return decodeURIComponent(tail);
  } catch {
    return tail;
  }
}

/**
 * On-frame placement of a clip in normalized units plus the pixels it occupies at export.
 * Layer x/y are centre coordinates, so the pixel box is offset by half the size.
 */
export function overlayClipGeometry(clip, aspectRatio = '9:16') {
  const layer = clip?.layer;
  if (!layer || typeof layer !== 'object') return null;
  const width = Number(layer.width);
  const height = Number(layer.height);
  const x = Number(layer.x);
  const y = Number(layer.y);
  if (![width, height, x, y].every(Number.isFinite)) return null;
  const frame = exportTargetSizeForAspect(aspectRatio);
  return {
    x,
    y,
    width,
    height,
    rotation: Number(layer.rotation) || 0,
    frameWidth: frame.width,
    frameHeight: frame.height,
    pixelWidth: Math.round(width * frame.width),
    pixelHeight: Math.round(height * frame.height),
    pixelLeft: Math.round((x - width / 2) * frame.width),
    pixelTop: Math.round((y - height / 2) * frame.height),
  };
}

/**
 * Everything the details popup shows for one overlay clip.
 * `fields` holds the flat label/value rows; richer parts (prompt, output) stay top-level.
 */
export function overlayClipDetails(clip, { aspectRatio = '9:16' } = {}) {
  if (!clip) return null;
  const start = Math.max(0, Number(clip.startTime) || 0);
  const end = hostOverlayClipEndTime(clip);
  const duration = Math.max(0, Number(clip.duration) || Math.max(0, end - start));
  const geometry = overlayClipGeometry(clip, aspectRatio);
  const outputUrl = hostOverlayPlaybackUrl(clip) || '';
  const laneLabel = overlayClipLaneLabel(clip);

  const fields = [
    { key: 'kind', label: 'Type', value: overlayClipKindLabel(clip) },
    { key: 'lane', label: 'Lane', value: laneLabel },
    { key: 'start', label: 'Starts', value: formatOverlayTimecode(start) },
    { key: 'end', label: 'Ends', value: formatOverlayTimecode(end) },
    { key: 'duration', label: 'Duration', value: formatOverlayDuration(duration) },
  ];

  if (geometry) {
    fields.push({
      key: 'frameSize',
      label: 'Frame size',
      value: `${formatPercent(geometry.width)} × ${formatPercent(geometry.height)} of frame`,
    });
    fields.push({
      key: 'exportPixels',
      label: 'Export pixels',
      value: `${geometry.pixelWidth} × ${geometry.pixelHeight} px (in ${geometry.frameWidth} × ${geometry.frameHeight})`,
    });
    fields.push({
      key: 'position',
      label: 'Position',
      value: `x ${formatPercent(geometry.x)} · y ${formatPercent(geometry.y)} (centre)`,
    });
    if (geometry.rotation) {
      fields.push({ key: 'rotation', label: 'Rotation', value: `${geometry.rotation.toFixed(1)}°` });
    }
  }

  if (Number.isFinite(Number(clip.opacity)) && Number(clip.opacity) !== 1) {
    fields.push({ key: 'opacity', label: 'Opacity', value: formatPercent(clip.opacity, 0) });
  }
  if (clip.muted) {
    fields.push({ key: 'muted', label: 'State', value: 'Hidden (off)' });
  }
  if (clip.sceneId) {
    fields.push({ key: 'sceneId', label: 'Snippet', value: String(clip.sceneId), copyable: true });
  }
  if (clip.suggestionId) {
    fields.push({ key: 'suggestionId', label: 'Suggestion ID', value: String(clip.suggestionId), copyable: true });
  }
  fields.push({ key: 'clipId', label: 'Clip ID', value: String(clip.id || ''), copyable: true });

  return {
    title: clip.name || clip.displayText || clip.triggerText || `${laneLabel} clip`,
    laneLabel,
    kindLabel: overlayClipKindLabel(clip),
    start,
    end,
    duration,
    geometry,
    prompt: String(clip.generationPrompt || ''),
    triggerText: String(clip.triggerText || ''),
    displayText: String(clip.displayText || clip.cardTitle || ''),
    animationStyle: sanitizeArtifactMotionStyle(clip.animationStyle),
    visualStyle: clip.visualStyle ? sanitizeBrollVisualStyle(clip.visualStyle) : '',
    hasBackground: clip.hasBackground,
    outputUrl,
    outputFileName: outputFileNameFromUrl(outputUrl),
    fields,
  };
}

export const MAX_OVERLAY_DISPLAY_TEXT = 160;

/**
 * Clip patch for an on-screen text edit made in the details popup.
 * A carousel card draws `cardTitle`, so it has to move with `displayText` — and the timeline
 * label carries the card number, which stays put even when the text is cleared.
 */
export function overlayClipDisplayTextPatch(clip, text) {
  const value = String(text ?? '').trim().slice(0, MAX_OVERLAY_DISPLAY_TEXT);
  if (!isCardOverlayClip(clip)) return { displayText: value };
  const number = String(clip?.cardNumber ?? '').trim();
  const label = value || String(clip?.cardTitle || 'Card');
  return {
    displayText: value,
    cardTitle: value,
    name: number ? `${number}. ${label}` : label,
  };
}
